import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withModifiers as _withModifiers, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "d-flex flex-wrap align-center" }
const _hoisted_2 = { class: "pa-1" }
const _hoisted_3 = { class: "d-flex ga-2 align-center" }

import { computed, reactive, ref, watch } from 'vue'
import type { FactoryScheduleDraft, FactoryScheduleRecord, NewFactorySchedulePayload } from '@/types/factories'
import {
  buildUtcDateOnReference,
  formatLocalDate as formatDateLocal,
  formatLocalTime as formatTimeLocal,
  getTimeZoneAbbreviation,
  type Local12HourTime,
  local12HourTimesToUtc,
  utcTimesToLocal12Hour
} from '@/utils/scheduleTime'


export default /*@__PURE__*/_defineComponent({
  __name: 'FactoryScheduleCard',
  props: {
    schedule: {},
    factoryId: {},
    isNew: { type: Boolean, default: false }
  },
  emits: ["save", "toggle", "delete", "cancel-new"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const localExpanded = ref(false)

const expanded = computed({
  get: () => props.isNew ? true : localExpanded.value,
  set: (value: boolean) => {
    localExpanded.value = value
  }
})
const localSchedule = reactive<FactoryScheduleRecord | FactoryScheduleDraft>(JSON.parse(JSON.stringify(props.schedule)))
const localTimesToRun = ref<Local12HourTime[]>([])

watch(
  () => props.schedule,
  (value) => {
    Object.assign(localSchedule, JSON.parse(JSON.stringify(value)))
    localTimesToRun.value = toLocalTimes(value)
  },
  { deep: true }
)

localTimesToRun.value = toLocalTimes(localSchedule)

const weekdayItems = [
  { title: 'Sun', value: 0 },
  { title: 'Mon', value: 1 },
  { title: 'Tue', value: 2 },
  { title: 'Wed', value: 3 },
  { title: 'Thu', value: 4 },
  { title: 'Fri', value: 5 },
  { title: 'Sat', value: 6 }
]

const weeklySelection = computed({
  get: () => localSchedule.daysOfWeek || [],
  set: (value: number[]) => {
    localSchedule.daysOfWeek = value
  }
})

function getReferenceDate(): Date {
  if (localSchedule.repeatInterval === 'once' && typeof localSchedule.startDate === 'number') {
    return new Date(localSchedule.startDate)
  }
  const timestamp =
    typeof localSchedule.nextRunAt === 'number'
      ? localSchedule.nextRunAt
      : typeof localSchedule.startDate === 'number'
        ? localSchedule.startDate
        : Date.now()
  return new Date(timestamp)
}

function toLocalTimes(schedule: FactoryScheduleRecord | FactoryScheduleDraft): Local12HourTime[] {
  const referenceDate = getReferenceDateFor(schedule)
  return utcTimesToLocal12Hour(schedule.timesToRun || [], referenceDate)
}

function getReferenceDateFor(schedule: FactoryScheduleRecord | FactoryScheduleDraft): Date {
  if (schedule.repeatInterval === 'once' && typeof schedule.startDate === 'number') {
    return new Date(schedule.startDate)
  }
  const timestamp =
    typeof schedule.nextRunAt === 'number'
      ? schedule.nextRunAt
      : typeof schedule.startDate === 'number'
        ? schedule.startDate
        : Date.now()
  return new Date(timestamp)
}

function getTimeDisplayDate(hour: number, minute: number): Date {
  const ref = getReferenceDate()
  return buildUtcDateOnReference(ref, { hour, minute })
}

function formatLocalEntryTime(time: Local12HourTime): string {
  const utcTime = local12HourTimesToUtc([time], getReferenceDate())[0]
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short'
  }).format(getTimeDisplayDate(utcTime.hour, utcTime.minute))
}

const localTimeZoneAbbreviation = computed(() => getTimeZoneAbbreviation(getReferenceDate()))

const scheduleSubtitle = computed(() => {
  const times = localTimesToRun.value
    .map((time) => formatLocalEntryTime(time))
    .join(', ')
  return `${localSchedule.repeatInterval} · ${times}`
})

const displayTimestamp = computed<number | null>(() => {
  if (localSchedule.repeatInterval === 'once') {
    const firstTime = localSchedule.timesToRun[0]
    if (!firstTime) return typeof localSchedule.startDate === 'number' ? localSchedule.startDate : null
    return getTimeDisplayDate(firstTime.hour, firstTime.minute).getTime()
  }

  return typeof localSchedule.nextRunAt === 'number'
    ? localSchedule.nextRunAt
    : typeof localSchedule.startDate === 'number'
      ? localSchedule.startDate
      : null
})

const displayTimestampLabel = computed(() => {
  if (displayTimestamp.value == null) return ''
  const date = formatDateLocal(displayTimestamp.value)
  const time = formatTimeLocal(displayTimestamp.value)
  return localSchedule.repeatInterval === 'once'
    ? `Scheduled ${date} • ${time}`
    : `Next run ${date} • ${time}`
})

function addTime() {
  localTimesToRun.value.push({ hour: 12, minute: 0, ampm: 'PM' })
}

function removeTime(index: number) {
  localTimesToRun.value.splice(index, 1)
}

function save() {
  localSchedule.timesToRun = local12HourTimesToUtc(localTimesToRun.value, getReferenceDate())
  if (props.isNew) {
    const payload: NewFactorySchedulePayload = {
      factoryConfigId: props.factoryId,
      label: localSchedule.label,
      startDate: localSchedule.startDate,
      timesToRun: localSchedule.timesToRun,
      repeatInterval: localSchedule.repeatInterval,
      daysOfWeek: localSchedule.repeatInterval === 'weekly' ? localSchedule.daysOfWeek : undefined,
      dayOfMonth: localSchedule.repeatInterval === 'monthly' ? localSchedule.dayOfMonth : undefined,
      status: localSchedule.status,
      initialCarryPayload: localSchedule.initialCarryPayload
    }
    emit('save', payload)
    return
  }
  emit('save', { ...localSchedule })
}

function emitToggle() {
  if (typeof localSchedule.id === 'string' && localSchedule.id) {
    emit('toggle', localSchedule.id)
  }
}

function emitDelete() {
  if (typeof localSchedule.id === 'string' && localSchedule.id) {
    emit('delete', localSchedule.id)
  }
}

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_avatar = _resolveComponent("v-avatar")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_card_subtitle = _resolveComponent("v-card-subtitle")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_item = _resolveComponent("v-card-item")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_number_input = _resolveComponent("v-number-input")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_textarea = _resolveComponent("v-textarea")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_expand_transition = _resolveComponent("v-expand-transition")!
  const _component_v_card = _resolveComponent("v-card")!

  return (_openBlock(), _createBlock(_component_v_card, {
    class: "ma-0 pa-2 card mb-2",
    color: "background",
    density: "compact",
    variant: "elevated",
    elevation: "4"
  }, {
    default: _withCtx(() => [
      (!expanded.value)
        ? (_openBlock(), _createBlock(_component_v_card_item, {
            key: 0,
            class: "schedule-summary",
            density: "compact",
            onClick: _cache[1] || (_cache[1] = ($event: any) => (expanded.value = true))
          }, {
            prepend: _withCtx(() => [
              _createVNode(_component_v_avatar, {
                size: "40",
                rounded: "lg",
                class: "mr-3 schedule-summary-avatar"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_icon, {
                    icon: "mdi-calendar-clock-outline",
                    size: "small"
                  })
                ]),
                _: 1
              })
            ]),
            append: _withCtx(() => [
              _createVNode(_component_v_btn, {
                color: "primary",
                density: "compact",
                icon: "mdi-chevron-down",
                size: "small",
                variant: "plain",
                onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (expanded.value = true), ["stop"]))
              })
            ]),
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, { class: "text-truncate" }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(localSchedule.label || 'New Factory Schedule'), 1)
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_subtitle, { class: "text-truncate" }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(scheduleSubtitle.value), 1)
                ]),
                _: 1
              })
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      (!expanded.value)
        ? (_openBlock(), _createBlock(_component_v_card_text, {
            key: 1,
            class: "pt-0 pb-2 schedule-summary-text"
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_1, [
                _createVNode(_component_v_chip, {
                  class: "mr-2 mb-2",
                  density: "compact",
                  color: localSchedule.status === 'enabled' ? 'success' : localSchedule.status === 'running' ? 'primary' : 'grey',
                  size: "small"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(localSchedule.status), 1)
                  ]),
                  _: 1
                }, 8, ["color"]),
                (displayTimestamp.value != null)
                  ? (_openBlock(), _createBlock(_component_v_chip, {
                      key: 0,
                      class: "mr-2 mb-2",
                      density: "compact",
                      size: "small",
                      variant: "tonal"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(displayTimestampLabel.value), 1)
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
              ])
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      _createVNode(_component_v_expand_transition, null, {
        default: _withCtx(() => [
          _withDirectives(_createElementVNode("div", _hoisted_2, [
            _createVNode(_component_v_text_field, {
              modelValue: localSchedule.label,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((localSchedule.label) = $event)),
              label: "Label",
              variant: "outlined",
              density: "compact",
              class: "pt-2"
            }, null, 8, ["modelValue"]),
            _createVNode(_component_v_select, {
              modelValue: localSchedule.repeatInterval,
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((localSchedule.repeatInterval) = $event)),
              items: ['once', 'daily', 'weekly', 'monthly'],
              label: "Frequency",
              variant: "outlined",
              density: "compact",
              class: "pt-2"
            }, null, 8, ["modelValue"]),
            _createVNode(_component_v_row, { density: "compact" }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(localTimesToRun.value, (time, index) => {
                  return (_openBlock(), _createBlock(_component_v_col, {
                    cols: "6",
                    class: "py-1",
                    key: index
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_3, [
                        _createVNode(_component_v_number_input, {
                          modelValue: time.hour,
                          "onUpdate:modelValue": ($event: any) => ((time.hour) = $event),
                          label: "Hour",
                          min: 1,
                          max: 12,
                          "control-variant": "stacked",
                          density: "compact",
                          "hide-details": ""
                        }, null, 8, ["modelValue", "onUpdate:modelValue"]),
                        _createVNode(_component_v_number_input, {
                          modelValue: time.minute,
                          "onUpdate:modelValue": ($event: any) => ((time.minute) = $event),
                          label: "Minute",
                          min: 0,
                          max: 59,
                          "control-variant": "stacked",
                          density: "compact",
                          "hide-details": ""
                        }, null, 8, ["modelValue", "onUpdate:modelValue"]),
                        _createVNode(_component_v_select, {
                          modelValue: time.ampm,
                          "onUpdate:modelValue": ($event: any) => ((time.ampm) = $event),
                          items: ['AM', 'PM'],
                          label: localTimeZoneAbbreviation.value,
                          density: "compact",
                          "hide-details": "",
                          variant: "outlined"
                        }, null, 8, ["modelValue", "onUpdate:modelValue", "label"]),
                        (localTimesToRun.value.length > 1)
                          ? (_openBlock(), _createBlock(_component_v_btn, {
                              key: 0,
                              density: "compact",
                              icon: "mdi-minus-circle",
                              variant: "text",
                              onClick: ($event: any) => (removeTime(index))
                            }, null, 8, ["onClick"]))
                          : _createCommentVNode("", true)
                      ])
                    ]),
                    _: 2
                  }, 1024))
                }), 128))
              ]),
              _: 1
            }),
            _cache[13] || (_cache[13] = _createElementVNode("div", { class: "text-caption text-medium-emphasis mb-2" }, " Times are shown in your local timezone and stored in UTC automatically. ", -1)),
            _createVNode(_component_v_btn, {
              size: "small",
              density: "compact",
              variant: "outlined",
              "prepend-icon": "mdi-plus",
              class: "mb-2",
              onClick: addTime
            }, {
              default: _withCtx(() => _cache[9] || (_cache[9] = [
                _createTextVNode("Add Time")
              ])),
              _: 1,
              __: [9]
            }),
            (localSchedule.repeatInterval === 'weekly')
              ? (_openBlock(), _createBlock(_component_v_select, {
                  key: 0,
                  modelValue: weeklySelection.value,
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((weeklySelection).value = $event)),
                  items: weekdayItems,
                  "item-title": "title",
                  "item-value": "value",
                  label: "Days of Week",
                  multiple: "",
                  chips: "",
                  variant: "outlined",
                  density: "compact",
                  class: "pt-2"
                }, null, 8, ["modelValue"]))
              : _createCommentVNode("", true),
            (localSchedule.repeatInterval === 'monthly')
              ? (_openBlock(), _createBlock(_component_v_number_input, {
                  key: 1,
                  modelValue: localSchedule.dayOfMonth,
                  "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((localSchedule.dayOfMonth) = $event)),
                  label: "Day of Month",
                  min: 1,
                  max: 31,
                  "control-variant": "stacked",
                  variant: "outlined",
                  density: "compact",
                  class: "pt-2"
                }, null, 8, ["modelValue"]))
              : _createCommentVNode("", true),
            _createVNode(_component_v_textarea, {
              modelValue: localSchedule.initialCarryPayload,
              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((localSchedule.initialCarryPayload) = $event)),
              label: "Initial Carry Payload",
              "auto-grow": "",
              rows: "2",
              variant: "outlined",
              density: "compact",
              class: "pt-2"
            }, null, 8, ["modelValue"]),
            _createVNode(_component_v_card_actions, { class: "px-0 py-1" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_btn, {
                  color: "primary",
                  density: "compact",
                  variant: "tonal",
                  onClick: save
                }, {
                  default: _withCtx(() => _cache[10] || (_cache[10] = [
                    _createTextVNode("Save")
                  ])),
                  _: 1,
                  __: [10]
                }),
                (!_ctx.isNew)
                  ? (_openBlock(), _createBlock(_component_v_btn, {
                      key: 0,
                      color: "primary",
                      density: "compact",
                      variant: "text",
                      disabled: localSchedule.status === 'running',
                      onClick: emitToggle
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(localSchedule.status === 'enabled' ? 'Disable' : localSchedule.status === 'disabled' ? 'Enable' : 'Running'), 1)
                      ]),
                      _: 1
                    }, 8, ["disabled"]))
                  : _createCommentVNode("", true),
                (!_ctx.isNew)
                  ? (_openBlock(), _createBlock(_component_v_btn, {
                      key: 1,
                      color: "error",
                      density: "compact",
                      variant: "text",
                      onClick: emitDelete
                    }, {
                      default: _withCtx(() => _cache[11] || (_cache[11] = [
                        _createTextVNode("Delete")
                      ])),
                      _: 1,
                      __: [11]
                    }))
                  : _createCommentVNode("", true),
                (_ctx.isNew)
                  ? (_openBlock(), _createBlock(_component_v_btn, {
                      key: 2,
                      density: "compact",
                      variant: "text",
                      onClick: _cache[7] || (_cache[7] = ($event: any) => (_ctx.$emit('cancel-new')))
                    }, {
                      default: _withCtx(() => _cache[12] || (_cache[12] = [
                        _createTextVNode("Cancel")
                      ])),
                      _: 1,
                      __: [12]
                    }))
                  : _createCommentVNode("", true),
                _createVNode(_component_v_spacer),
                _createVNode(_component_v_btn, {
                  density: "compact",
                  icon: expanded.value ? 'mdi-chevron-up' : 'mdi-chevron-down',
                  onClick: _cache[8] || (_cache[8] = ($event: any) => (expanded.value = !expanded.value))
                }, null, 8, ["icon"])
              ]),
              _: 1
            })
          ], 512), [
            [_vShow, expanded.value]
          ])
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})